import { DataEmFormatoISO, DataEmUnixTimestamp, EmpregadoId, IConfiguracaoSmartTable, ID } from '@/tipos'
import { GerenciamentoDeFeriasServico } from './gerenciamentoDeFeriasServico'
import { StatusSolicitacao } from '@/typescript/modulos/governanca/solicitacoes/dominio/solicitacoes.enum'

export interface IRequisicaoSolicitarFerias {
  idEmpregado: EmpregadoId
  qtdAbonoPecuniario: number | null | undefined
  adiantamentoDecimoTerceiro: boolean
  inicio: Date
  fim: Date
  ignorarValidacoesDoGestaoDeFerias?: boolean
}

export interface IRespostaSolicitarFerias {
  data: {
    mensagem: string
  }
}

type SolicitacaoId = string
type FeriasAgendadaId = string
type StringDataNoFormatoISO = string
type TotalDeItens = number
type TotalDePaginas = number

export enum TipoEntidadeFiltrEnum {
  Lider = 'LIDER',
  LocalDeTrabalho = 'LOCAL_DE_TRABALHO',
  GrupoDeLocais = 'GRUPO_DE_LOCAIS',
  Empregado = 'EMPREGADO',
  Empregador = 'EMPREGADOR',
}

export interface ISolicitacao {
  id: FeriasAgendadaId,
  status: StatusSolicitacao,
  inicio: StringDataNoFormatoISO,
  fim: StringDataNoFormatoISO
}

export interface IFeriasAgendada {
  id: SolicitacaoId,
  status: StatusSolicitacao,
  inicio: StringDataNoFormatoISO,
  fim: StringDataNoFormatoISO
}

/**
 * Objeto que representa as ferias no
 * calendario de ferias do frontend
 */
export interface IFeriasCalendario {
  id: ID;
  idEmpregado: ID;
  inicio: DataEmUnixTimestamp;
  fim: DataEmUnixTimestamp;
  legenda: string;
  quantidadeDeDias: number;
  tipo: ITipoDeFeriasCalendario;
}

export type IFeriasAgendadasCompleta = {
  id: ID;
  intervaloDoEvento: IntervaloDoEvento;
  empregado: Empregado;
  erroNaSolicitacao: null;
  ultimaAcaoExecutada: string | null;
  acaoExecutadaPor: string;
  acaoExecutadaEm: Date;
  geradaPorSolicitacao: boolean;
  foiSolicitadoAdiantamentoDoDecimoTerceiro: boolean;
  numeroDeDiasDeFeriasVendidos: number;
}

export type IFeriasCriadaPeloAgente = Pick<IFeriasAgendadasCompleta, 'id' | 'intervaloDoEvento'> & { empregado: { id: EmpregadoId } }

export type ISaldoAVencer = {
  id: ID,
  empregado: Empregado,
  periodoAquisitivo: {
    inicio: DataEmFormatoISO,
    fim: DataEmFormatoISO
  },
  periodoConcessivo: {
    inicio: DataEmFormatoISO,
    fim: DataEmFormatoISO
  },
  saldoDeFeriasUsado: number,
}

export type IFeriasSolicitadasCompleta = {
  tipoDeEvento: "ferias";
  status: string;
  solicitacao: string;
  cienteDeAutoAprovacao: boolean;
  motivo: string;
} & Omit<IFeriasAgendadasCompleta, 'geradaPorSolicitacao'>

export type IFeriasAgendadasCompletaResponse = {
  resultado: IFeriasAgendadasCompleta[]
  totalDeItens: TotalDeItens
  totalDePaginas: TotalDePaginas
}

export type IFeriasSolicitadasCompletaResponse = {
  resultado: IFeriasSolicitadasCompleta[]
  totalDeItens: TotalDeItens
  totalDePaginas: TotalDePaginas
}

export type IFeriasSaldosAVencerResponse = {
  resultado: ISaldoAVencer[]
  totalDeItens: TotalDeItens
  totalDePaginas: TotalDePaginas
}

export interface Empregado {
  id: string;
  nome: string;
}

export interface IntervaloDoEvento {
  inicio: DataEmFormatoISO;
  fim: DataEmFormatoISO;
}


export interface IRespostaHistoricoDeSolicitacoes {
  data: {
    resultado: ISolicitacao[]
  }
}

export interface IRespostaHistoricoDeSolicitacoesPaginada {
  data: {
    resultado: ISolicitacao[],
    totalDeItens: TotalDeItens,
    totalDePaginas: TotalDePaginas
  }
}

export interface IRespostaHistoricoDeFeriasAgendadasPaginada {
  data: {
    resultado: IFeriasAgendada[],
    totalDeItens: TotalDeItens,
    totalDePaginas: TotalDePaginas
  }
}

export interface IParametrosDaBuscaPaginadaApiHistoricoDeSolicitacoes {
  page: number,
  perPage: number
}

export interface IRetornoPaginaDaTabelaApiHistoricoDeSolicitacoes {
  items: ISolicitacao[],
  totalItems: TotalDeItens,
  totalPages: TotalDePaginas
}

export interface IErroAoSolicitarFerias {
  response: {
    data: {
      description: string,
      error: string
    } | null
  } | null
}

export interface ICorpoRequisicaoFerias {
  dataInicial: Date,
  dataFinal: Date,
  status?: StatusSolicitacao
  entidades: {
    tipo: string,
    ids: string[]
  },
  paginacao: {
    pagina: number,
    por_pagina: number
  }
}

export type ICorpoRequisicaoSaldosAVencer = Omit<ICorpoRequisicaoFerias, 'dataInicial' | 'dataFinal'> & {
  quantidadeDeDiasAteOVencimento: number
}

export enum TipoDeFeriasCalendarioEnum {
  agendadas = 'agendadas',
  solicitadas = 'solicitadas',
  sugestaoDoAgente = 'sugestaoDoAgente',
  sugestaoDoAgenteAprovar = 'sugestaoDoAgenteAprovar',
  sugestaoDoAgenteReprovar = 'sugestaoDoAgenteReprovar'
}

export type ITipoDeFeriasCalendario = keyof typeof TipoDeFeriasCalendarioEnum


export interface Data {
  dataInicio: Date,
  dataFim: Date,
  carregandoCriacaoSolicitacaoDeFerias: boolean,
  carregandoBuscaDoHistorico: boolean,
  configuracaoTabelaDeHistorico: IConfiguracaoSmartTable,
  historicoDeSolicitacoes: ISolicitacao[],
  verFormulario: boolean,
  gerenciamentoDeFeriasServico: GerenciamentoDeFeriasServico,
  solicitacaoDeFeriasPorId: { [key: string]: ISolicitacao }
}

export interface Computed {
  tituloDaPagina: string,
  dataMinimaDosSeletores: Date,
  tabelaDeHistorico: { loading: boolean; loaded: ISolicitacao[] },
  idEmpregadoDoUsuarioLogado: EmpregadoId | null
}

export interface Methods {
  solicitarFerias: () => Promise<void>,
  obterHistoricoDeSolicitacoesPaginado: (
    parametrosRequisicao: IParametrosDaBuscaPaginadaApiHistoricoDeSolicitacoes,
  ) => Promise<IRetornoPaginaDaTabelaApiHistoricoDeSolicitacoes>,
  formatarDataParaMostrar: (data: Date) => string,
  formatarDuracao: (intervaloDoEvento: { inicio: Date; fim: Date }) => string,
  cancelar: () => void,
  mostrarFormulario: () => void,
  atualizarHistorico: () => void,
  aoClicarNaSolicitacao: (solicitacaoDeFerias: ISolicitacao) => void
}

export {
  StatusSolicitacao
}
